import { useHelper } from "@hooks/useHelper";
import { useImage } from "@hooks/useImage";
import { useRoutes } from "@hooks/useRoutes";
import { ComponentProps, ResolvedLink } from "@ts/components";
import { ContentSection, SanityKeyed } from "@ts/sanity";
import React, { FC } from "react";

export type ImageGridCardInputProps = ComponentProps & {
  cols?: number;
  contents: Array<SanityKeyed<ContentSection>>;
  handleClick: () => void;
  spacing?: boolean;
  textColour?: string;
};

export type ImageGridCardOutputProps = Omit<
  ImageGridCardInputProps,
  "contents" | "handleClick"
> & {
  images?: any;
  handleClickImage: (imageLink: ResolvedLink) => void;
};

export const withImageGridCard =
  (Component: FC<ImageGridCardOutputProps>) =>
  ({
    name = "ImageGridCard",
    contents,
    spacing,
    cols,
    textColour,
    handleClick,
  }: ImageGridCardInputProps) => {
    const { linkResolver } = useRoutes();
    const { getFluidImage } = useImage();
    const { handleClickOnCustomContentLink } = useHelper();

    const images = contents?.map(({ image, title }) => {
      const imageLink: ResolvedLink =
        image?.imagelink && image?.imagelink?.length
          ? linkResolver(image?.imagelink[0])
          : null;

      return {
        image: getFluidImage(image),
        imageLink: imageLink,
        imageLinkTitle: imageLink?.title,
        imageTitle: title,
      };
    });

    const handleClickImage = (imageLink) => {
      handleClick();
      handleClickOnCustomContentLink(imageLink);
    };

    Component.displayName = name;

    return (
      <Component
        spacing={spacing}
        cols={cols}
        images={images}
        handleClickImage={handleClickImage}
        textColour={textColour}
      />
    );
  };
